import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  FileText,
  Search,
  RefreshCw,
  Download,
  Filter,
  Calendar,
  Shield,
  User,
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  ChevronLeft,
  ChevronRight,
  Loader2,
  LogIn,
  LogOut,
  UserPlus,
  Key,
  CreditCard,
  Settings,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuditLogs = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [securitySummary, setSecuritySummary] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    action: '',
    severity: '',
    user_id: '',
    start_date: '',
    end_date: ''
  });

  const actionCategories = {
    authentication: ['LOGIN_SUCCESS', 'LOGIN_FAILED', 'LOGOUT', 'PASSWORD_CHANGE'],
    twoFactor: ['2FA_ENABLED', '2FA_DISABLED', '2FA_VERIFIED', '2FA_FAILED'],
    users: ['USER_CREATED', 'USER_UPDATED', 'USER_DELETED', 'USER_ROLE_CHANGED'],
    transactions: ['TRANSACTION_INITIATED', 'TRANSACTION_APPROVED', 'TRANSACTION_REJECTED', 'TRANSACTION_FAILED'],
    beneficiaries: ['BENEFICIARY_ADDED', 'BENEFICIARY_UPDATED', 'BENEFICIARY_DELETED'],
    rules: ['RULE_CREATED', 'RULE_UPDATED', 'RULE_DELETED'],
    security: ['SUSPICIOUS_ACTIVITY', 'RATE_LIMIT_EXCEEDED', 'UNAUTHORIZED_ACCESS']
  };

  const allActions = Object.values(actionCategories).flat();

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString()
      });

      if (filters.action) params.append('action', filters.action);
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const res = await fetch(`${API_URL}/api/audit/logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [token, page, pageSize, filters]);

  const fetchSecuritySummary = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/audit/logs/security-events?hours=24`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        setSecuritySummary(await res.json());
      }
    } catch (error) {
      console.error('Error fetching security summary:', error);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchLogs();
      fetchSecuritySummary();
    }
  }, [token, fetchLogs, fetchSecuritySummary]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      // Fetch all logs for export (up to 1000)
      const params = new URLSearchParams({
        page: '1',
        page_size: '1000'
      });

      if (filters.action) params.append('action', filters.action);
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const res = await fetch(`${API_URL}/api/audit/logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        const exportLogs = data.logs || [];

        // Generate CSV
        const headers = ['Timestamp', 'Action', 'Severity', 'User', 'IP Address', 'Success', 'Details'];
        const rows = exportLogs.map(log => [
          log.timestamp,
          log.action,
          log.severity,
          log.user_email || 'N/A',
          log.ip_address,
          log.success ? 'Yes' : 'No',
          JSON.stringify(log.details || {})
        ]);

        const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);

        toast.success('Audit logs exported successfully');
      }
    } catch (error) {
      toast.error('Failed to export audit logs');
    } finally {
      setExporting(false);
    }
  };

  const getActionIcon = (action) => {
    if (actionCategories.authentication.includes(action)) {
      return action.includes('SUCCESS') ? <LogIn className="w-4 h-4 text-green-500" /> : <LogOut className="w-4 h-4 text-slate-400" />;
    }
    if (actionCategories.twoFactor.includes(action)) {
      return <Shield className="w-4 h-4 text-blue-500" />;
    }
    if (actionCategories.users.includes(action)) {
      return <UserPlus className="w-4 h-4 text-purple-500" />;
    }
    if (actionCategories.transactions.includes(action)) {
      return <CreditCard className="w-4 h-4 text-emerald-500" />;
    }
    if (actionCategories.beneficiaries.includes(action)) {
      return <User className="w-4 h-4 text-cyan-500" />;
    }
    if (actionCategories.rules.includes(action)) {
      return <Settings className="w-4 h-4 text-orange-500" />;
    }
    if (actionCategories.security.includes(action)) {
      return <AlertTriangle className="w-4 h-4 text-red-500" />;
    }
    return <Activity className="w-4 h-4 text-slate-400" />;
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      LOW: 'bg-blue-100 text-blue-700',
      MEDIUM: 'bg-yellow-100 text-yellow-700',
      HIGH: 'bg-orange-100 text-orange-700',
      CRITICAL: 'bg-red-100 text-red-700'
    };
    return <Badge className={colors[severity] || 'bg-slate-100 text-slate-700'}>{severity}</Badge>;
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="audit-logs-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
            <p className="text-gray-500">Track all security events and user activities</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={fetchLogs}
              className="gap-2"
              data-testid="refresh-logs"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={handleExportCSV}
              disabled={exporting}
              className="gap-2 bg-blue-600 hover:bg-blue-700"
              data-testid="export-logs-csv"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export CSV
            </Button>
          </div>
        </div>

        {/* Security Summary Cards */}
        {securitySummary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Failed Logins (24h)</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {securitySummary.events?.LOGIN_FAILED?.count || 0}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                    <XCircle className="w-5 h-5 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">2FA Failures (24h)</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {securitySummary.events?.['2FA_FAILED']?.count || 0}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Rate Limited (24h)</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {securitySummary.events?.RATE_LIMIT_EXCEEDED?.count || 0}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Events (24h)</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {securitySummary.total_events || 0}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">Filters:</span>
              </div>
              
              <Select
                value={filters.action || 'all'}
                onValueChange={(v) => {
                  setFilters({ ...filters, action: v === 'all' ? '' : v });
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-48" data-testid="filter-action">
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {allActions.map(action => (
                    <SelectItem key={action} value={action}>
                      {action.replace(/_/g, ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.severity || 'all'}
                onValueChange={(v) => {
                  setFilters({ ...filters, severity: v === 'all' ? '' : v });
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-36" data-testid="filter-severity">
                  <SelectValue placeholder="All Severities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severities</SelectItem>
                  <SelectItem value="LOW">Low</SelectItem>
                  <SelectItem value="MEDIUM">Medium</SelectItem>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="CRITICAL">Critical</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => {
                    setFilters({ ...filters, start_date: e.target.value });
                    setPage(1);
                  }}
                  className="w-36"
                  placeholder="Start Date"
                  data-testid="filter-start-date"
                />
                <span className="text-gray-400">to</span>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => {
                    setFilters({ ...filters, end_date: e.target.value });
                    setPage(1);
                  }}
                  className="w-36"
                  placeholder="End Date"
                  data-testid="filter-end-date"
                />
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFilters({ action: '', severity: '', user_id: '', start_date: '', end_date: '' });
                  setPage(1);
                }}
                className="text-gray-500"
              >
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Activity Logs
            </CardTitle>
            <CardDescription>
              Showing {logs.length} of {total} entries
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[180px]">Timestamp</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>IP Address</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[80px]">Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log, idx) => (
                      <TableRow key={log.log_id || idx} className="cursor-pointer hover:bg-gray-50" onClick={() => setSelectedLog(log)}>
                        <TableCell className="text-sm text-gray-500 font-mono">
                          {new Date(log.timestamp).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getActionIcon(log.action)}
                            <span className="text-sm font-medium">{log.action?.replace(/_/g, ' ')}</span>
                          </div>
                        </TableCell>
                        <TableCell>{getSeverityBadge(log.severity)}</TableCell>
                        <TableCell className="text-sm text-gray-600">{log.user_email || 'N/A'}</TableCell>
                        <TableCell className="text-sm font-mono text-gray-500">{log.ip_address}</TableCell>
                        <TableCell>
                          {log.success ? (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="w-3 h-3 mr-1" /> Success
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700">
                              <XCircle className="w-3 h-3 mr-1" /> Failed
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {logs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-12 text-gray-500">
                          No audit logs found for the selected filters
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-sm text-gray-500">
                      Page {page} of {totalPages}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Log Detail Sheet */}
        <Sheet open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
          <SheetContent className="w-[500px] sm:w-[600px]">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                {selectedLog && getActionIcon(selectedLog.action)}
                Log Details
              </SheetTitle>
              <SheetDescription>
                Complete information about this audit event
              </SheetDescription>
            </SheetHeader>
            
            {selectedLog && (
              <div className="mt-6 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-500">Timestamp</label>
                    <p className="font-mono text-sm">{new Date(selectedLog.timestamp).toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Action</label>
                    <p className="font-medium">{selectedLog.action?.replace(/_/g, ' ')}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Severity</label>
                    <div className="mt-1">{getSeverityBadge(selectedLog.severity)}</div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Status</label>
                    <p className="mt-1">
                      {selectedLog.success ? (
                        <Badge className="bg-green-100 text-green-700">Success</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700">Failed</Badge>
                      )}
                    </p>
                  </div>
                </div>

                <div className="space-y-4 border-t pt-4">
                  <div>
                    <label className="text-sm text-gray-500">User Email</label>
                    <p className="font-medium">{selectedLog.user_email || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">IP Address</label>
                    <p className="font-mono">{selectedLog.ip_address}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">User Agent</label>
                    <p className="text-sm text-gray-600 break-all">{selectedLog.user_agent}</p>
                  </div>
                </div>

                {selectedLog.resource_type && (
                  <div className="space-y-2 border-t pt-4">
                    <div>
                      <label className="text-sm text-gray-500">Resource Type</label>
                      <p className="font-medium">{selectedLog.resource_type}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Resource ID</label>
                      <p className="font-mono text-sm">{selectedLog.resource_id}</p>
                    </div>
                  </div>
                )}

                {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                  <div className="border-t pt-4">
                    <label className="text-sm text-gray-500">Additional Details</label>
                    <pre className="mt-2 p-3 bg-gray-50 rounded-lg text-sm overflow-auto max-h-48">
                      {JSON.stringify(selectedLog.details, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </DashboardLayout>
  );
};

export default AuditLogs;
